import { mix, modelPosition, mx_noise_float, positionWorld, smoothstep, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { GravityField } from '../gravity/GravityField';
import { mulberry32 } from '../math/rng';
import type { TunnelDef } from './Tunnel';

/**
 * Stalactites along a tunnel. They grow from dripping water, so each hangs along
 * the local gravity: they lean as "down" turns through the cave, shrink as gravity
 * fades toward the saddle, and there are none where you float. Placed on the part
 * of the wall that is "up" at each point, so they never block the walkway.
 * Instances are relative to `anchor` (anchor the mesh there).
 */
export function buildStalactites(
  tunnel: Pick<TunnelDef, 'path' | 'radius' | 'name'>,
  field: GravityField,
  anchor: THREE.Vector3,
  lightsNode: THREE.LightsNode,
  /** True where the tube is out in the open (above a mouth plateau): nothing grows there. */
  outside: (p: THREE.Vector3) => boolean,
  seed = 3,
  /** Gravity (m/s²) at which they grow full length (they shrink as it fades). */
  fullAt = 8,
): THREE.InstancedMesh {
  const geo = new THREE.ConeGeometry(0.28, 1, 7, 3);
  // Tip down (-Y), base at the origin.
  geo.rotateX(Math.PI);
  geo.translate(0, -0.5, 0);
  const pos = geo.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const wobble = 1 + 0.25 * Math.sin(y * 9 + pos.getX(i) * 5);
    pos.setX(i, pos.getX(i) * wobble);
    pos.setZ(i, pos.getZ(i) * wobble);
  }
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.45, metalness: 0 });
  mat.lightsNode = lightsNode;
  const p = positionWorld.sub(modelPosition);
  mat.colorNode = mix(
    vec3(0.2, 0.17, 0.15),
    vec3(0.46, 0.41, 0.35),
    smoothstep(-0.5, 0.6, mx_noise_float(p.mul(1.7))),
  );

  const rnd = mulberry32(seed);

  const path = tunnel.path;
  const r = tunnel.radius;
  const g = new THREE.Vector3();
  const down = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const side = new THREE.Vector3();
  const offset = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const matrices: THREE.Matrix4[] = [];
  const Y = new THREE.Vector3(0, -1, 0);
  for (let i = 8; i < path.length - 8; i += 2) {
    if (rnd() > 0.6 || outside(path[i])) continue;
    field.sample(path[i], g);
    const strength = g.length();
    if (strength < 0.19 * fullAt) continue; // nothing drips where you float
    down.copy(g).divideScalar(strength);
    tangent.subVectors(path[i + 1], path[i - 1]).normalize();
    // "Up" across the tube, then scattered around it (never near the floor).
    offset.copy(down).negate();
    offset.addScaledVector(tangent, -offset.dot(tangent)).normalize();
    side.crossVectors(tangent, offset).normalize();
    const a = (rnd() * 2 - 1) * 1.0;
    offset.multiplyScalar(Math.cos(a)).addScaledVector(side, Math.sin(a));
    const base = path[i]
      .clone()
      .addScaledVector(offset, r - 0.12)
      .sub(anchor);
    const length = (0.5 + rnd() * 1.8) * Math.min(1, strength / fullAt);
    q.setFromUnitVectors(Y, down);
    const w = 0.6 + rnd() * 0.8;
    matrices.push(new THREE.Matrix4().compose(base, q, new THREE.Vector3(w, length, w)));
  }
  const mesh = new THREE.InstancedMesh(geo, mat, matrices.length);
  for (const [i, m] of matrices.entries()) mesh.setMatrixAt(i, m);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.frustumCulled = false;
  mesh.name = `${tunnel.name} stalactites`;
  return mesh;
}
